import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import ArticleHero from '@/pages/article/components/ArticleHero';
import ArticleBody from '@/pages/article/components/ArticleBody';
import ArticleCTA from '@/pages/article/components/ArticleCTA';
import { getArticleBySlug } from '@/mocks/article';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : null;

  if (!article) {
    return (
      <>
        <PageMeta title="文章未找到 - 51黑料吃瓜网" description="您访问的文章不存在" />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-32 pb-16 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">返回首页</Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.subtitle,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '51黑料吃瓜网',
      url: 'https://51heiliaochigua.com',
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `https://51heiliaochigua.com/article/${slug}` },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51heiliaochigua.com' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://51heiliaochigua.com/category/${slug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://51heiliaochigua.com/article/${slug}` },
    ],
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - 51黑料吃瓜网 | 51爆料网`}
        description={article.subtitle}
        keywords={`${article.category},${article.title},51黑料吃瓜网,51爆料网,娱乐圈八卦,吃瓜群众`}
        canonical={`https://51heiliaochigua.com/article/${slug}`}
        ogTitle={article.title}
        ogDescription={article.subtitle}
        ogType="article"
        ogUrl={`https://51heiliaochigua.com/article/${slug}`}
        twitterTitle={article.title}
        twitterDescription={article.subtitle}
        jsonLdScripts={[articleJsonLd, breadcrumbJsonLd]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <nav className="pt-20 md:pt-28 px-6 md:px-10 max-w-4xl mx-auto" aria-label="面包屑导航">
          <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
            <li><Link to="/" className="hover:text-foreground-950 transition-colors">首页</Link></li>
            <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
            <li><Link to={`/category/${slug}`} className="hover:text-foreground-950 transition-colors">{article.category}</Link></li>
            <li><i className="ri-arrow-right-s-line text-[10px]"></i></li>
            <li className="text-foreground-950 font-medium truncate max-w-[200px]">{article.title}</li>
          </ol>
        </nav>

        <main>
          <article>
            <ArticleHero
              title={article.title}
              subtitle={article.subtitle}
              category={article.category}
              date={article.date}
              author={article.author}
              heat={article.heat}
              image={article.image}
              slug={slug || ''}
            />
            <ArticleBody contentSections={article.contentSections} />
            <ArticleCTA />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}